import { createPortal } from 'react-dom';
import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  'aria-label'?: string;
}

/**
 * Custom dropdown select matching BillerBay brand colors.
 * Uses a portal so the menu is never clipped by overflow containers.
 */
export function Select({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  disabled,
  className,
  id,
  ...rest
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 200 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const inButton = buttonRef.current?.contains(e.target as Node);
      const inMenu = menuRef.current?.contains(e.target as Node);
      if (!inButton && !inMenu) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleToggle = () => {
    if (disabled) return;
    if (!buttonRef.current) { setOpen((o) => !o); return; }
    const rect = buttonRef.current.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    setOpen((o) => !o);
  };

  return (
    <div className={cn('relative', className)}>
      <button
        ref={buttonRef}
        type="button"
        id={id}
        disabled={disabled}
        onClick={handleToggle}
        className={cn(
          'w-full flex items-center justify-between gap-2 border border-bb-lilac bg-white px-3 py-2 text-sm text-left transition-colors',
          'focus:outline-none focus:ring-1 focus:ring-bb-navy/30 focus:border-bb-navy',
          disabled && 'opacity-50 cursor-not-allowed',
          !disabled && 'hover:border-bb-navy/50',
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={rest['aria-label']}
      >
        <span className={cn('truncate', !selected && 'text-slate-400')}>
          {selected?.label ?? placeholder}
        </span>
        <ChevronDown
          size={14}
          className={cn(
            'shrink-0 text-slate-400 transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>

      {open && createPortal(
        <ul
          ref={menuRef}
          role="listbox"
          style={{
            position: 'fixed',
            top: menuPos.top,
            left: menuPos.left,
            width: menuPos.width,
            zIndex: 9999,
          }}
          className="max-h-60 overflow-y-auto bg-white border border-bb-lilac/60 shadow-lg custom-scrollbar"
        >
          {placeholder && (
            <li
              role="option"
              aria-selected={!value}
              onClick={() => { onChange(''); setOpen(false); }}
              className={cn(
                'px-3 py-2 text-sm cursor-pointer transition-colors',
                !value
                  ? 'bg-[var(--bb-active)] text-white'
                  : 'text-slate-400 hover:bg-[var(--bb-hover)] hover:text-white',
              )}
            >
              {placeholder}
            </li>
          )}
          {options.map((opt) => {
            const isActive = opt.value === value;
            return (
              <li
                key={opt.value}
                role="option"
                aria-selected={isActive}
                aria-disabled={opt.disabled}
                onClick={() => {
                  if (opt.disabled) return;
                  onChange(opt.value);
                  setOpen(false);
                }}
                className={cn(
                  'px-3 py-2 text-sm cursor-pointer transition-colors',
                  opt.disabled && 'opacity-40 cursor-not-allowed',
                  isActive
                    ? 'bg-[var(--bb-active)] text-white font-medium'
                    : 'text-slate-700 hover:bg-[var(--bb-hover)] hover:text-white',
                )}
              >
                <span className="block truncate">{opt.label}</span>
                {opt.description && (
                  <span className={cn(
                    'block text-[11px] truncate mt-0.5',
                    isActive ? 'text-white/70' : 'text-slate-400',
                  )}>
                    {opt.description}
                  </span>
                )}
              </li>
            );
          })}
        </ul>,
        document.body,
      )}
    </div>
  );
}
